import { autocomplete } from '@algolia/autocomplete-js';

import { sectionHintSearchPlugin } from '../search/sectionHintSearchPlugin';
import { recentSearchesSearchPlugin } from '../search/recentSearchesSearchPlugin';
// import createQuerySuggestions from '../search/querySuggestionsSearchPlugin';
import { createCategoriesSearchPlugin } from '../search/categoriesSearchPlugin';
import { createBrandStoreSearchPlugin } from '../search/brandStoreSearchPlugin';
import { createProductSearchPlugin } from '../search/productSearchPlugin';
import { fallbackQueryPerSectionSearchPlugin } from '../search/fallbackQueryPerSectionSearchPlugin';

import { getResponsiveBreakpoint } from '../util/breakpoint';
import * as tracking from '../util/tracking';

// Algolia methods we want to bring to outer-scope
let _setIsOpen;
let _refresh;

// On the index page the searchbox animates first to the top
// so opening up the autocomplete dropdown should be delayed
// a bit
const shouldDelayOpeningOfAutocomplete =
  window.cece.show_searchbox_in_page_header &&
  !['xs', 'sm', 'md'].includes(getResponsiveBreakpoint());
let shouldOpen = !shouldDelayOpeningOfAutocomplete;

let searchClient = new window.TypesenseSearchClient({
  apiKey: window.cece.typesense.searchAPIKey, // Be sure to use an API key that only allows search operations
  nodes: [
    {
      host: 'tori51mnjpx6d40sp-1.a1.typesense.net',
      port: '443',
      protocol: 'https',
    },
  ],
  connectionTimeoutSeconds: 2,
});

function init() {
  const searchParams = new URL(window.location).searchParams;
  const initialState = {};
  if (searchParams.has('query')) {
    initialState.query = searchParams.get('query');
  }

  ({ setIsOpen: _setIsOpen, refresh: _refresh } = autocomplete({
    container: '#autocomplete-catalog-search',
    placeholder: window.i18n.header.searchPlaceholder,
    initialState,
    plugins: [
      sectionHintSearchPlugin,
      recentSearchesSearchPlugin,
      // createQuerySuggestions({ searchClient }),
      createCategoriesSearchPlugin({ searchClient }),
      createBrandStoreSearchPlugin({ searchClient }),
      createProductSearchPlugin({ searchClient }),
      fallbackQueryPerSectionSearchPlugin,
    ],
    openOnFocus: true,
    detachedMediaQuery: window.cece.show_searchbox_in_page_header
      ? '(max-width: 991px)'
      : '(max-width: 767px)',
    shouldPanelOpen({ state }) {
      const numberOfItems = state.collections.reduce((count, c) => {
        count += c.items.length;
        return count;
      }, 0);
      return numberOfItems > 0 && shouldOpen;
    },
    onSubmit({ state }) {
      const { query } = state;
      if (query) {
        let result = '/catalog/search?';
        if (window.cece.searchSection) {
          result += 'section=' + window.cece.searchSection + '&';
        }
        result += `query=${query}`;
        window.location.href = result;
        tracking.trackEvent('CatalogSearch', 'ManualSearch', query);
      }
    },
    // debug: true,
  }));
}

// Wrapper function which makes sure the setIsOpen
// method is actually available
function setIsOpen(value) {
  if (_setIsOpen) {
    _setIsOpen(value);
  }
}

// Wrapper function which makes sure the refresh
// method is actually available
function refresh() {
  if (_refresh) {
    _refresh();
  }
}

// Open the search manually
function openSearch() {
  setIsOpen(true);
  refresh();
}

// Only initialize if the container element is on the page
const el = document.getElementById('autocomplete-catalog-search');
if (el) {
  init();

  // The code below make sure the autocomplete dropdown
  // opens 300ms after the input field has been focussed
  // so that the input field can first animate upwards
  // (so that there is more space for the dropdown)
  if (shouldDelayOpeningOfAutocomplete) {
    const input = el.querySelector('.aa-Input');
    if (input) {
      input.addEventListener('focus', () => {
        shouldOpen = false;
        setTimeout(() => {
          shouldOpen = true;
          openSearch();
        }, 500);
      });
      input.addEventListener('blur', () => {
        shouldOpen = false;
      });
    }
  }
}

export { openSearch };
